import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"
import { cn } from "@/lib/utils"
import { Button } from "./button"

// Icon button variants - extending the base button variants
const iconButtonVariants = cva(
  "inline-flex items-center justify-center p-0 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary: "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        sm: "size-8",
        default: "size-9",
        lg: "size-10",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "ghost",
      size: "default",
    },
  },
)

// Icon button component
type IconButtonProps = React.ComponentProps<typeof Button> &
  VariantProps<typeof iconButtonVariants> & {
    icon: React.ReactNode
    label: string // For accessibility
  }

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ label, icon, variant, size, className, ...props }, ref) => {
    return (
      <Button
        ref={ref}
        variant={variant}
        className={cn(iconButtonVariants({ variant, size, className }))}
        aria-label={label}
        {...props}
      >
        {icon}
      </Button>
    )
  },
)

IconButton.displayName = "IconButton"

// Loading spinner component
const LoadingSpinner = ({ className }: { className?: string }) => (
  <svg
    className={cn("size-4", className)}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.25" />
    <path d="M12 2a10 10 0 0 1 0 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
)

export { IconButton, iconButtonVariants, LoadingSpinner }
